import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { Loader2, AlertCircle, CheckCircle, XCircle, Store, Printer } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { errorMessage, reportError } from '../../../lib/errors';
import AdditionalWorkApproval, { type AdditionalWorkPayload } from './AdditionalWorkApproval';
import { mileageLine } from '../Customers/mileage';
import { DEFAULT_DOCUMENT_ACCENT } from '../../../lib/brandAccent';

// The quote-link RPCs refuse a quote that can no longer be decided, with a
// sentence written for the customer. Show that; anything else is our fault.
const QUOTE_STATE_MESSAGE = /declined|not been sent|expired|no longer valid|already been accepted/i;

function quoteActionError(err: unknown, fallback: string): string {
    const msg = errorMessage(err, '');
    return QUOTE_STATE_MESSAGE.test(msg) ? msg : fallback;
}

export default function PublicQuoteView() {
    const { token: tokenParam } = useParams<{ token: string }>();
    const location = useLocation();
    // This view is mounted directly by App.tsx on `/quote/*` (not inside a
    // matched <Route path="/quote/:token">), so useParams() has no :token
    // segment to read. Derive the token from the pathname as the source of
    // truth, keeping useParams() as a fallback for when it *is* route-matched.
    const token = tokenParam || location.pathname.replace(/^\/+quote\/+/, '').split('/')[0] || undefined;
    const [searchParams] = useSearchParams();
    const decisionHint = searchParams.get('decision'); // 'accept' | 'decline' from the email buttons
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [accepting, setAccepting] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [rejected, setRejected] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [showRejectReason, setShowRejectReason] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    useEffect(() => {
        const fetchQuote = async () => {
            if (!token) {
                setError('Invalid quote link');
                setLoading(false);
                return;
            }

            const { data, error } = await supabase.rpc('fn_get_quote_by_token', {
                p_token: token
            });

            if (error) {
                console.error(error);
                setError('Failed to load quote');
            } else if (!data) {
                setError('Quote not found');
            } else {
                setData(data);
                if (data.quote.status === 'accepted' || data.quote.status === 'converted') {
                    setAccepted(true);
                } else if (data.quote.status === 'rejected') {
                    setRejected(true);
                }
            }
            setLoading(false);
        };

        fetchQuote();
    }, [token]);

    // Arriving via the "Decline" button in the email opens the reason box.
    useEffect(() => {
        if (decisionHint === 'decline' && data && !accepted && !rejected) {
            setShowRejectReason(true);
        }
    }, [decisionHint, data, accepted, rejected]);

    const handleAccept = async () => {
        if (!token) return;
        setAccepting(true);
        try {
            const { error } = await supabase.rpc('fn_accept_quote_publicly', {
                p_token: token,
                p_user_agent: navigator.userAgent
            });

            if (error) throw error;
            setAccepted(true);
            setData((prev: any) => ({
                ...prev,
                quote: { ...prev.quote, status: 'accepted' }
            }));
        } catch (err) {
            const msg = quoteActionError(err, 'Failed to accept quote. Please try again.');
            if (!QUOTE_STATE_MESSAGE.test(msg)) reportError(err, 'public-quote.accept');
            toast.error(msg);
        } finally {
            setAccepting(false);
        }
    };

    const handleReject = async () => {
        if (!token) return;
        setRejecting(true);
        try {
            const { error } = await supabase.rpc('fn_reject_quote_publicly', {
                p_token: token,
                p_reason: rejectReason.trim() || null,
                p_user_agent: navigator.userAgent
            });

            if (error) throw error;
            setRejected(true);
            setShowRejectReason(false);
            setData((prev: any) => ({
                ...prev,
                quote: { ...prev.quote, status: 'rejected' }
            }));
        } catch (err) {
            const msg = quoteActionError(err, 'Failed to submit your response. Please try again.');
            if (!QUOTE_STATE_MESSAGE.test(msg)) reportError(err, 'public-quote.reject');
            toast.error(msg);
        } finally {
            setRejecting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-canvas flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-accent animate-spin" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen bg-canvas flex flex-col items-center justify-center p-4">
                <AlertCircle className="w-12 h-12 text-danger mb-4" />
                <h1 className="text-xl font-bold text-fg mb-2">Unavailable</h1>
                <p className="text-fg-4">{error || 'Quote not found'}</p>
            </div>
        );
    }

    // Extra work found on a job shares the link and RPCs but is its own decision.
    if (data.quote.quote_type === 'additional_work' && token) {
        return <AdditionalWorkApproval token={token} initial={data as AdditionalWorkPayload} decisionHint={decisionHint} />;
    }

    const { quote, organization } = data;
    const metadata = quote.metadata || {};
    const lineItems = metadata.lineItems || [];
    const customer = metadata.customer || {};
    const vehicle = metadata.vehicle || {};

    const total = quote.total_amount || 0;
    // Audit finding M-06. Was `subtotal = total / 1.2` unconditionally, so a
    // NON-VAT-REGISTERED sole trader's PUBLIC, customer-facing quote displayed
    // VAT that had never been charged and that the trader is not authorised to
    // show (VATA 1994 s.67). The VAT rate is not persisted on the quote, so
    // derive the split from the net line items: unregistered -> line sum equals
    // total -> vat is 0 and the row is hidden; registered -> the difference is
    // the VAT actually charged, at whatever rate applied.
    const subtotal = lineItems.length > 0
        ? lineItems.reduce(
            (sum: number, li: any) =>
                sum + (Number(li.quantity) || 0) * (Number(li.unitPrice ?? li.unit_price) || 0),
            0,
          )
        : total;
    const vat = Math.round((total - subtotal) * 100) / 100;
    const showVat = Math.abs(vat) >= 0.01;
    const accentColor = organization.branding?.invoice_accent_color || organization.branding?.primary_color || DEFAULT_DOCUMENT_ACCENT;
    const isMinimal = organization.branding?.invoice_theme === 'minimal';

    return (
        <div className="min-h-screen bg-canvas pb-20 print:bg-transparent print:pb-0">
            {/* Header/Banner */}
            <div
                data-theme={isMinimal ? 'paper' : undefined}
                className="border-b border-tint/10 sticky top-0 z-10 px-4 py-4 md:px-8 print:static"
                style={isMinimal
                    ? { backgroundColor: 'var(--fd-surface)', borderBottom: `4px solid ${accentColor}` }
                    : { backgroundColor: accentColor }
                }
            >
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {organization.branding?.logo_url ? (
                            <img data-theme="paper" src={organization.branding.logo_url} className="h-8 md:h-10 object-contain bg-card rounded p-1" alt="Logo" />
                        ) : (
                            <div className="w-10 h-10 bg-tint/10 rounded flex items-center justify-center">
                                <Store className="w-6 h-6 text-fg" />
                            </div>
                        )}
                        <div>
                            <h2 className={`font-bold ${isMinimal ? 'text-fg' : 'text-on-fill'}`}>{organization.name}</h2>
                            <p className={`text-xs ${isMinimal ? 'text-fg-2' : 'text-on-fill/70'}`}>Quote #{quote.public_token?.slice(0, 8).toUpperCase()}</p>
                        </div>
                    </div>
                    <div className="print:hidden">
                        <button
                            onClick={() => window.print()}
                            className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${isMinimal
                                ? 'bg-raised hover:bg-pressed text-fg'
                                : 'bg-on-fill/10 hover:bg-on-fill/20 text-on-fill'
                                }`}
                        >
                            <Printer className="w-4 h-4" />
                            <span className="hidden sm:inline">Print</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
                <div data-theme="paper" className="relative bg-card rounded-xl shadow-xl overflow-hidden print:shadow-none print:rounded-none">
                    {/* Decision watermark (proof) */}
                    {(accepted || rejected) && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-20">
                            <span
                                className={`text-6xl md:text-8xl font-black uppercase tracking-widest opacity-10 -rotate-[20deg] select-none ${accepted ? 'text-success' : 'text-danger'}`}
                            >
                                {accepted ? 'Accepted' : 'Declined'}
                            </span>
                        </div>
                    )}
                    {/* Document Header */}
                    <div className="p-8 md:p-12 border-b border-line flex flex-col md:flex-row justify-between gap-8">
                        <div>
                            <h1 className="text-3xl font-bold text-fg mb-4">Quote</h1>
                            <div className="space-y-1 text-sm text-fg-2">
                                <p><span className="font-medium text-fg">Date:</span> {new Date(quote.created_at).toLocaleDateString()}</p>
                                <p><span className="font-medium text-fg">Valid Until:</span> {new Date(new Date(quote.created_at).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-sm space-y-1 text-fg-2">
                                {organization.branding?.address && <p className="whitespace-pre-line">{organization.branding.address}</p>}
                                {organization.branding?.phone_number && <p>{organization.branding.phone_number}</p>}
                                {organization.branding?.contact_email && <p>{organization.branding.contact_email}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Customer & Quote Details */}
                    <div className="p-8 md:p-12 bg-raised/50 flex flex-col md:flex-row justify-between gap-8">
                        <div>
                            <h3 className="text-xs font-bold text-fg-3 uppercase tracking-wider mb-3">Prepared For</h3>
                            <p className="font-bold text-fg text-lg">{customer.name}</p>
                            <p className="text-fg text-sm">{customer.email}</p>
                            <p className="text-fg text-sm">{customer.phone}</p>
                        </div>
                        <div className="md:text-right">
                            <h3 className="text-xs font-bold text-fg-3 uppercase tracking-wider mb-3">Vehicle</h3>
                            <p className="font-bold text-fg text-lg font-mono">{vehicle.vrm}</p>
                            <p className="text-fg text-sm">{vehicle.make} {vehicle.model}</p>
                            <p className="text-fg-2 text-sm mt-1">{vehicle.year ? `${vehicle.year} • ` : ''}{vehicle.engine ? `${vehicle.engine} • ` : ''}{mileageLine(vehicle.mileage, vehicle.mileageSource, vehicle.mileageMotDate) ?? ''}</p>
                        </div>
                    </div>

                    {/* Line Items */}
                    <div className="px-8 md:px-12 py-4">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b-2 border-line text-xs text-fg-3 uppercase tracking-wider text-left">
                                    <th className="py-4 font-medium">Description</th>
                                    <th className="py-4 font-medium text-center w-20">Qty</th>
                                    <th className="py-4 font-medium text-right w-32">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-line">
                                {lineItems.map((item: any, i: number) => (
                                    <tr key={i}>
                                        <td className="py-4">
                                            <p className="font-medium text-fg">{item.description}</p>
                                            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${item.type === 'labour' ? 'bg-gold/10 text-gold' : 'bg-info/10 text-info'
                                                }`}>
                                                {item.type}
                                            </span>
                                        </td>
                                        <td className="py-4 text-center text-fg font-mono">{item.quantity}</td>
                                        <td className="py-4 text-right font-bold text-fg font-mono">£{(item.quantity * item.unitPrice).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Totals */}
                    <div className="px-8 md:px-12 py-8 bg-raised border-t border-line">
                        <div className="flex flex-col md:items-end gap-2">
                            <div className="flex justify-between w-full md:w-64 text-sm text-fg">
                                <span>Subtotal</span>
                                <span>£{subtotal.toFixed(2)}</span>
                            </div>
                            {showVat && (
                                <div className="flex justify-between w-full md:w-64 text-sm text-fg">
                                    <span>VAT</span>
                                    <span>£{vat.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex justify-between w-full md:w-64 text-xl font-bold text-fg pt-2 border-t border-line mt-2">
                                <span>Total</span>
                                <span style={{ color: accentColor }}>£{total.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Acceptance Action */}
                <div className="mt-8 flex justify-center print:hidden">
                    {accepted ? (
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="bg-success-fill text-on-fill px-8 py-4 rounded-xl shadow-lg flex items-center gap-3"
                        >
                            <CheckCircle className="w-8 h-8" />
                            <div>
                                <h3 className="font-bold text-lg">Quote Accepted</h3>
                                <p>The team has been notified.</p>
                            </div>
                        </motion.div>
                    ) : rejected ? (
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="bg-pressed text-fg px-8 py-4 rounded-xl shadow-lg flex items-center gap-3"
                        >
                            <XCircle className="w-8 h-8 text-danger" />
                            <div>
                                <h3 className="font-bold text-lg">Quote Declined</h3>
                                <p className="text-fg-4">Thanks for letting us know. The workshop has been notified.</p>
                            </div>
                        </motion.div>
                    ) : (
                        <div className="w-full max-w-md bg-panel rounded-xl p-6 border border-tint/10 shadow-2xl">
                            {/* Decline is its own screen, not an extra control under
                                a big green ACCEPT. Following "Decline" from the email
                                used to land the customer on a card headed "Ready to
                                proceed?" with an accept button on top of the reason
                                box they had been sent there to fill in — two opposite
                                calls to action at once. The email's two buttons now
                                each open the one thing they promised. */}
                            {showRejectReason ? (
                                <>
                                    <h3 className="text-fg font-bold text-lg mb-2">Decline this quote</h3>
                                    <p className="text-fg-4 text-sm mb-4">
                                        The workshop will be told straight away. A short reason
                                        helps them come back with something that works — it's
                                        optional.
                                    </p>
                                    <textarea
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        rows={3}
                                        placeholder="Optional: let us know why (price, timing, changed my mind…)"
                                        className="w-full bg-sunken border border-tint/20 rounded-lg px-3 py-2 text-fg text-sm placeholder:text-fg-4 focus:outline-none focus:border-tint/40 resize-none"
                                    />
                                    <button
                                        onClick={handleReject}
                                        disabled={rejecting}
                                        className="w-full mt-3 py-4 bg-danger-fill hover:bg-danger-fill text-on-fill font-bold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                                    >
                                        {rejecting ? <Loader2 className="w-5 h-5 animate-spin" /> : <XCircle className="w-5 h-5" />}
                                        CONFIRM DECLINE
                                    </button>
                                    <button
                                        onClick={() => { setShowRejectReason(false); setRejectReason(''); }}
                                        disabled={rejecting}
                                        className="w-full mt-3 py-3 bg-transparent text-fg-4 hover:text-fg font-medium rounded-lg border border-tint/20 hover:border-tint/40 transition-all text-sm disabled:opacity-50"
                                    >
                                        Actually, I want to accept it
                                    </button>
                                </>
                            ) : (
                                <>
                                    <h3 className="text-fg font-bold text-lg mb-2">Ready to proceed?</h3>
                                    <p className="text-fg-4 text-sm mb-6">Accepting confirms you agree to the works listed above — we'll then contact you to schedule the job. If it's not right, you can decline and tell us why.</p>
                                    <button
                                        onClick={handleAccept}
                                        disabled={accepting || rejecting}
                                        className="w-full py-4 text-on-fill font-bold rounded-lg hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ backgroundColor: accentColor }}
                                    >
                                        {accepting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                                        ACCEPT QUOTE
                                    </button>
                                    <button
                                        onClick={() => setShowRejectReason(true)}
                                        disabled={accepting || rejecting}
                                        className="w-full mt-3 py-3 bg-transparent text-fg-4 hover:text-fg font-medium rounded-lg border border-tint/20 hover:border-tint/40 transition-all text-sm disabled:opacity-50"
                                    >
                                        Decline quote
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Print Styles Override */}
            <style>{`
                @media print {
                    body { background: none !important; }
                    .print\\:hidden { display: none !important; }
                    .max-w-4xl { max-width: none !important; padding: 0 !important; margin: 0 !important; }
                    .shadow-xl, .shadow-2xl { box-shadow: none !important; }
                }
            `}</style>
        </div>
    );
}
